import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpError } from '@models/http-error.model';
import { CookieWrapper } from './cookie.service';
import { environment } from '@environments/environment';

export type QueryParam =
  | string
  | number
  | boolean
  | null
  | undefined
  | QueryParam[]
  | Record<string, any>;

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private readonly http = inject(HttpClient);
  private readonly cookieService = inject(CookieWrapper);

  get<T>(
    endpoint: string,
    params?: Record<string, QueryParam>,
    headers?: Record<string, string>,
    asBlob?: false,
  ): Observable<T>;
  get(
    endpoint: string,
    params?: Record<string, QueryParam>,
    headers?: Record<string, string>,
    asBlob?: true,
  ): Observable<Blob>;
  get<T>(
    endpoint: string,
    params?: Record<string, QueryParam>,
    headers?: Record<string, string>,
    asBlob?: boolean,
  ): Observable<T | Blob> {
    if (asBlob) {
      return this.http
        .get(endpoint, {
          headers: this.buildHeaders(undefined, true),
          params: this.buildParams(params),
          responseType: 'blob',
        })
        .pipe(catchError(this.handleError));
    }
    return this.http
      .get<T>(endpoint, {
        headers: this.buildHeaders(headers),
        params: this.buildParams(params),
      })
      .pipe(catchError(this.handleError));
  }

  post<T, B = unknown>(
    endpoint: string,
    body?: B,
    headers?: Record<string, string>,
    asBlob?: false,
  ): Observable<T>;
  post<B = unknown>(
    endpoint: string,
    body?: B,
    headers?: Record<string, string>,
    asBlob?: true,
  ): Observable<Blob>;
  post<T, B = unknown>(
    endpoint: string,
    body?: B,
    headers?: Record<string, string>,
    asBlob?: boolean,
  ): Observable<T | Blob> {
    if (asBlob) {
      return this.http
        .post(endpoint, body, {
          headers: this.buildHeaders(undefined, true),
          responseType: 'blob',
        })
        .pipe(catchError(this.handleError));
    }
    return this.http
      .post<T>(endpoint, body, {
        headers: this.buildHeaders(headers),
      })
      .pipe(catchError(this.handleError));
  }

  put<T, B = unknown>(endpoint: string, body?: B, headers?: Record<string, string>): Observable<T> {
    return this.http
      .put<T>(endpoint, body, {
        headers: this.buildHeaders(headers),
      })
      .pipe(catchError(this.handleError));
  }

  patch<T, B = unknown>(
    endpoint: string,
    body?: B,
    headers?: Record<string, string>,
  ): Observable<T> {
    return this.http
      .patch<T>(endpoint, body, { headers: this.buildHeaders(headers) })
      .pipe(catchError(this.handleError));
  }

  delete<T, B = unknown>(
    endpoint: string,
    body?: B,
    headers?: Record<string, string>,
  ): Observable<T> {
    return this.http
      .delete<T>(endpoint, {
        body,
        headers: this.buildHeaders(headers),
      })
      .pipe(catchError(this.handleError));
  }

  private buildHeaders(headers?: Record<string, string>, asBlob?: boolean): HttpHeaders {
    const base: Record<string, string> = asBlob
      ? {}
      : { 'Content-Type': 'application/json', Accept: 'application/json' };

    if (this.cookieService.check('caisse_token')) {
      const token = this.cookieService.get('caisse_token');
      base['Authorization'] = `Bearer ${token}`;
    } else if (this.cookieService.check('caisse_checkin_token')) {
      const token = this.cookieService.get('caisse_checkin_token');
      base['Authorization'] = `Bearer ${token}`;
    }

    if (environment.isFullOnline) {
      base['X-Consumer'] = 'self';
    }

    Object.assign(base, headers);

    let httpHeaders = new HttpHeaders();
    Object.entries(base).forEach(([key, value]) => {
      httpHeaders = httpHeaders.set(key, value);
    });

    return httpHeaders;
  }

  private buildParams(params?: Record<string, QueryParam>): HttpParams {
    let httpParams = new HttpParams();

    if (!params) {
      return httpParams;
    }

    const appendParam = (key: string, value: any) => {
      if (value === null || value === undefined) {
        return;
      }

      // tableau
      if (Array.isArray(value)) {
        value.forEach((v, index) => {
          if (typeof v === 'object') {
            Object.entries(v).forEach(([subKey, subValue]) => {
              appendParam(`${key}[${index}][${subKey}]`, subValue);
            });
          } else {
            httpParams = httpParams.append(`${key}[]`, String(v));
          }
        });
        return;
      }

      // objet
      if (typeof value === 'object') {
        Object.entries(value).forEach(([subKey, subValue]) => {
          appendParam(`${key}[${subKey}]`, subValue);
        });
        return;
      }

      // valeur simple
      httpParams = httpParams.append(key, String(value));
    };

    Object.entries(params).forEach(([key, value]) => {
      appendParam(key, value);
    });

    return httpParams;
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    const httpError: HttpError = {
      status: error.status,
      message: error.error?.message || error.message || 'Une erreur est survenue',
      error: error.error,
    };

    return throwError(() => httpError);
  };
}
